/**
 * WordPress dependencies
 */
import { useSelect } from '@safe-wordpress/data';
import { store as BLOCK_EDITOR } from '@safe-wordpress/block-editor';
import { getBlockType, getBlockVariations } from '@safe-wordpress/blocks';
import type { IconKey } from '@safe-wordpress/components';
import type { BlockInstance } from '@safe-wordpress/blocks';

/**
 * External dependencies
 */
import { isFunction, isEqual, pick, isObject, isString, has } from 'lodash';

export type FieldBlock = {
	readonly id: string;
	readonly label: string;
	readonly name: string;
	readonly type: string;
	readonly icon: IconKey;
};

export const useFieldBlocks = (
	clientId?: string
): ReadonlyArray< FieldBlock > => {
	return useSelect( ( select ) => {
		const {
			getBlocksByClientId,
			getClientIdsOfDescendants,
			getClientIdsWithDescendants,
		} = select( BLOCK_EDITOR );

		const clientIds = clientId
			? getClientIdsOfDescendants( [ clientId ] )
			: getClientIdsWithDescendants();
		const blocks = getBlocksByClientId( clientIds );

		const fieldBlocks = blocks
			.filter( isFieldBlock )
			.map( extractFieldBlockData );

		return fieldBlocks;
	} );
};

const isFieldBlock = (
	b: BlockInstance | null
): b is BlockInstance< FieldBlock > =>
	!! b &&
	b.name.startsWith( 'nelio-forms/' ) &&
	b.name !== 'nelio-forms/form' &&
	! b.attributes.disabled;

const extractFieldBlockData = (
	b: BlockInstance< FieldBlock >
): FieldBlock => ( {
	id: b.attributes.id,
	label: b.attributes.label || b.attributes.id,
	name: getName( b ),
	type: getType( b ),
	icon: getIcon( b ),
} );

const getType = (
	block: BlockInstance< FieldBlock >
): 'text' | 'email' | 'url' => {
	if ( block.attributes.type === 'email' ) {
		return 'email';
	} else if ( block.attributes.type === 'url' ) {
		return 'url';
	} //end if
	return 'text';
};

const getName = ( block: BlockInstance< FieldBlock > ): string =>
	getActiveVariation( block )?.name || block.name;

const getIcon = ( block: BlockInstance< FieldBlock > ): IconKey => {
	const blockType = getBlockType( block.name );
	const activeVariation = getActiveVariation( block );

	return (
		getIconSrc( activeVariation?.icon ) ??
		getIconSrc( blockType?.icon ) ??
		'editor-code'
	);
};

const getIconSrc = ( icon: unknown ): IconKey | undefined => {
	if ( isString( icon ) ) {
		return icon as IconKey;
	} //end if

	const hasSrc = ( o: unknown ): o is { src: unknown } =>
		isObject( o ) && has( o, 'src' );

	if ( hasSrc( icon ) && isString( icon.src ) ) {
		return icon.src as IconKey;
	} //end if

	return undefined;
};

const getActiveVariation = ( block: BlockInstance< FieldBlock > ) => {
	const variations = getBlockVariations( block.name ) || [];

	return variations.find( ( v ) => {
		if ( isFunction( v.isActive ) ) {
			return v.isActive( block.attributes, v.attributes );
		} //end if

		const expected = pick( v.attributes, v.isActive || [] );
		const actual = pick( block.attributes, v.isActive || [] );
		return isEqual( expected, actual );
	} );
};
